import { BACKEND_URL, mediaUrl, profilePicUrl } from '../../config';
import RiskoMeter from '../RiskoMeter/RiskoMeter';
import React, { useState, useEffect, useRef } from 'react';
import DefaultPFP from '../../Media/Icons/DefaultPFP.webp';
import UnknownAuthority from '../../Media/Icons/UnknownAuthority.webp';
import Anonymous from '../../Media/Icons/anonymous.png';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { formatCustomDistanceToNow } from '../../utils/timeUtils';
import { useUser } from '../../UserContext';
import { useData } from '../../DataContext';
import Header from '../Header/Header';
import {
	Gauge,
	ArrowUp,
	ArrowDown,
	Download,
	Upload,
	Send,
	Clock,
	MessageCircle,
	RotateCcw,
	XCircle,
	Search,
	PlayCircle,
	Hourglass,
} from 'lucide-react';
import './MyReports.css';
import ForwardReportModal from '../ForwardReportModal/ForwardReportModal';
import CardDescription from '../AllPosts/CardDescription';
import ReportsKanban from './ReportsKanban';
import {
	ReportTab,
	TAB_LABELS,
	TabCounts,
	EMPTY_COUNTS,
	countForTab,
	buildReportsUrl as buildSharedReportsUrl,
	fetchTabCounts,
	renderHighlightedText,
	ReportNumberChip,
	ViewModeSwitch,
	DragScrollRow,
	closedByMe,
} from './reportShared';

// Define MediaType and PostType
type MediaType = {
	format: string;
	internalURL: string;
	latitude: string;
	longitude: string;
};

type AuthoritySummary = {
	id: number;
	name: string;
	authority_phoneNumber?: number;
	profilepicURL: string | null;
	organisation?: string | null;
};

type ReportType = {
	id: number;
	title: string;
	body?: string;
	media: MediaType[];
	isAnonymous: boolean;
	updatedAt: string;
	createdAt: string;
	commentCount: number;
	isClosed?: boolean;
	inProgress?: boolean;
	status?: 'received' | 'in_progress' | 'closed';
	reportNumber?: string | null;
	closedAt?: string | null;
	user: {
		name: string;
		id: number;
		profilepicURL: string;
	};
	// Legacy/compat fields (kept so older callers still work):
	authority?: AuthoritySummary | null;
	// New chain fields from backend:
	originalAuthority?: AuthoritySummary | null;
	currentAuthority?: AuthoritySummary | null;
	forwardedFromAuthority?: AuthoritySummary | null;
	latestForwardedToAuthority?: AuthoritySummary | null;
	closedByAuthority?: AuthoritySummary | null;
	forwardHistory?: any[];
};

export type AllReportDataType = {
	id: number;
	title: string;
	content?: string;
	format: string;
	image_url: any;
	media_filename: string;
	name: string;
	location: string;
	time: string;
	commentCount: number;
	profilepicURL: string;
	isClosed?: boolean;
	inProgress?: boolean;
	// The VIEWING authority's own bookkeeping number — never another's.
	reportNumber?: string | null;
	isAnonymous: boolean;
	// Backend chain fields surfaced for card-level tags:
	forwardedFromName?: string | null;
	latestForwardedToName?: string | null;
	closedByName?: string | null;
	closedByUserId?: number | null;
	closedAt?: string | null;
};

type Tab = ReportTab;

const ReportPlaceholderSVG = () => (
	<div className="w-full h-full bg-gray-100 overflow-hidden relative flex items-center justify-center animate-shimmer-wave">
		<svg className="w-32 h-32 text-gray-300 z-10 drop-shadow-sm relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	</div>
);

const ReportImage = ({ src, alt, className }: { src: string; alt: string; className?: string }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);
	// IntersectionObserver-gated src: strict top-to-bottom fetch order as the
	// user scrolls, so images don't all race to load at once.
	const [shouldLoad, setShouldLoad] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (shouldLoad) return;
		const el = containerRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '300px 0px' }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [shouldLoad]);

	if (hasError)
		return (
			<div className={`${className || ''} aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100`}>
				<ReportPlaceholderSVG />
			</div>
		);

	return (
		<div
			ref={containerRef}
			className={`relative aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100 ${className || ''}`}
		>
			{!isLoaded && (
				<div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
					<ReportPlaceholderSVG />
				</div>
			)}
			{shouldLoad && (
				<img
					src={src}
					alt={alt}
					decoding='async'
					className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
					onLoad={() => setIsLoaded(true)}
					onError={() => setHasError(true)}
				/>
			)}
		</div>
	);
};

const ProfileImage = ({
	name,
	pfpURL,
	className,
	isAnonymous,
}: {
	name: string;
	pfpURL?: string | null;
	className?: string;
	isAnonymous?: boolean;
}) => {
	const isAuth = name.toLowerCase().includes('authority');
	const [hasError, setHasError] = useState(false);

	// Initial src decision
	const src = isAnonymous
		? Anonymous
		: !hasError && pfpURL && pfpURL !== 'null' && pfpURL !== ''
			? profilePicUrl(pfpURL)
			: isAuth
				? UnknownAuthority
				: DefaultPFP;

	return <img src={src} alt={name} className={className} onError={() => setHasError(true)} />;
};

function MyReports() {
	const { user } = useUser();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const tabParam = searchParams.get('tab');

	const VALID_TABS: Tab[] = ['sent', 'received', 'progress', 'active', 'closed'];

	// Tab state: default to URL param, otherwise 'sent' for normal users, 'received' for authority
	const [activeTab, setActiveTab] = useState<Tab>(() => {
		if (VALID_TABS.includes(tabParam as Tab)) return tabParam as Tab;
		// Back-compat: redirect the now-removed 'forwarded' tab to 'active'
		if (tabParam === 'forwarded') return 'active';
		return user && !user.is_authority ? 'sent' : 'received';
	});

	// Switch to 'sent' if user is not authority after loading
	useEffect(() => {
		if (user && !user.is_authority && activeTab !== 'sent') {
			setActiveTab('sent');
		}
	}, [user, activeTab]);

	// Resolve the persisted tab key for sessionStorage lookups. Only the real tabs are valid.
	const resolveTabKey = (): Tab => {
		if (VALID_TABS.includes(tabParam as Tab)) return tabParam as Tab;
		if (tabParam === 'forwarded') return 'active';
		return 'received';
	};

	// Reports list for the active tab. Every tab now paginates with infinite
	// scroll (limit/offset), so this holds only the pages fetched so far.
	const [AllReportData, set_AllReportData] = useState<AllReportDataType[]>(() => {
		const saved = sessionStorage.getItem(`reportsFeedState_${resolveTabKey()}`);
		if (saved) {
			try {
				return JSON.parse(saved).AllReportData || [];
			} catch (e) { }
		}
		return [];
	});
	// 1-based page of the active tab, and whether more pages may exist. Restored
	// from sessionStorage so a back-navigation keeps the user's loaded depth.
	const [page, setPage] = useState<number>(() => {
		const saved = sessionStorage.getItem(`reportsFeedState_${resolveTabKey()}`);
		if (saved) {
			try {
				return JSON.parse(saved).page || 1;
			} catch (e) { }
		}
		return 1;
	});
	const [hasMore, setHasMore] = useState<boolean>(() => {
		const saved = sessionStorage.getItem(`reportsFeedState_${resolveTabKey()}`);
		if (saved) {
			try {
				return JSON.parse(saved).hasMore !== false;
			} catch (e) { }
		}
		return true;
	});

	const [isLoading, setIsLoading] = useState(false);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
	const [riskoMeterMediaId, setRiskoMeterMediaId] = useState<string | null>(null);
	const [toastMessage, setToastMessage] = useState<string | null>(null);
	const [reportToClose, setReportToClose] = useState<AllReportDataType | null>(null);
	const [reportToReopen, setReportToReopen] = useState<AllReportDataType | null>(null);
	const [reportToActivate, setReportToActivate] = useState<AllReportDataType | null>(null);

	const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
	const [selectedReportIdForForward, setSelectedReportIdForForward] = useState<number | null>(null);

	// Search box, sitting ABOVE the tabs: the term is deliberately kept when the
	// user switches tabs, so they can chase one report across all five.
	//
	// Seeded from sessionStorage so opening a result and pressing Back returns to
	// the SAME filtered list — the per-tab page cache below is restored the same
	// way, and restoring a filtered list next to an empty search box would look
	// broken. Read (not consumed) here; the key is cleared by the mount effect.
	const [searchQuery, setSearchQuery] = useState<string>(
		() => sessionStorage.getItem('reportsSearchQuery') || ''
	);
	// The term the currently-loaded pages were fetched with. Every fetch reads
	// this rather than `searchQuery`, so a page-2 request issued mid-debounce
	// can't append results for a different term than page 1. Seeded to match the
	// restored term, so the restore doesn't look like a change and trigger a
	// refetch that would discard the restored pages.
	const activeSearchRef = useRef<string>(
		sessionStorage.getItem('reportsSearchQuery') || ''
	);
	useEffect(() => {
		sessionStorage.removeItem('reportsSearchQuery');
	}, []);
	const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
	const isFirstSearchEffect = useRef<boolean>(true);
	const [tabCounts, setTabCounts] = useState<TabCounts>(EMPTY_COUNTS);

	// Interactive Dashboard (kanban) toggle. Desktop + authority only.
	//
	// The preference is keyed BY USER ID. A shared browser is normal here (a
	// caretaker's desk machine), and a global key meant the next account
	// inherited whatever view the previous one had chosen. Per-user keys mean a
	// fresh account always starts on its own default — and there's no logout hook
	// to remember to clear.
	//
	// Default for an authority is the board: it's the view built for handling
	// work, so it's what they should land on until they say otherwise.
	const viewPrefKey = (id: number | string) => `reportsViewMode:${id}`;
	const [isKanbanView, setIsKanbanView] = useState<boolean>(false);
	// Which account's preference is currently loaded, so the effect below applies
	// it exactly once per account instead of fighting the user's own toggling.
	const loadedPrefForUser = useRef<number | null>(null);
	useEffect(() => {
		if (!user?.id) return;
		const uid = Number(user.id);
		if (loadedPrefForUser.current === uid) return;
		loadedPrefForUser.current = uid;
		const pref = localStorage.getItem(viewPrefKey(uid));
		setIsKanbanView(pref ? pref === 'kanban' : !!user.is_authority);
	}, [user?.id, user?.is_authority]);

	// Single entry point for the switch, so both views record the choice the same
	// way and neither can forget to persist it.
	const setViewMode = (kanban: boolean) => {
		if (user?.id) {
			localStorage.setItem(viewPrefKey(Number(user.id)), kanban ? 'kanban' : 'list');
		}
		setIsKanbanView(kanban);
	};
	// Four side-by-side draggable columns need real width and a pointer; below
	// `lg` we fall back to the list view even if the preference is set, rather
	// than shipping a cramped board nobody can drag on.
	const [isDesktop, setIsDesktop] = useState<boolean>(
		() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
	);
	useEffect(() => {
		const mq = window.matchMedia('(min-width: 1024px)');
		const sync = () => setIsDesktop(mq.matches);
		// Both signals: matchMedia is the precise one, but it doesn't fire under a
		// devtools/CDP viewport override, and a board left rendered at 800px is
		// unusable. `resize` is the belt to that suspenders — `sync` is idempotent
		// and setState with an unchanged boolean is a no-op, so double-firing costs
		// nothing.
		mq.addEventListener('change', sync);
		window.addEventListener('resize', sync);
		sync();
		return () => {
			mq.removeEventListener('change', sync);
			window.removeEventListener('resize', sync);
		};
	}, []);
	const showKanban = isKanbanView && isDesktop && !!user?.is_authority;

	useEffect(() => {
		if (reportToClose || reportToReopen || reportToActivate || isForwardModalOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [reportToClose, reportToReopen, reportToActivate, isForwardModalOpen]);

	const closeModalRef = useRef<HTMLDivElement>(null);
	const reopenModalRef = useRef<HTMLDivElement>(null);
	const activateModalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			const activeModal = reportToClose
				? closeModalRef
				: reportToReopen
					? reopenModalRef
					: reportToActivate
						? activateModalRef
						: null;
			if (!activeModal || !activeModal.current) return;

			if (e.key === 'Escape') {
				if (reportToClose) setReportToClose(null);
				if (reportToReopen) setReportToReopen(null);
				if (reportToActivate) setReportToActivate(null);
				return;
			}
			if (e.key === 'Tab') {
				const focusableElements = activeModal.current.querySelectorAll(
					'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
				) as NodeListOf<HTMLElement>;

				if (!focusableElements || focusableElements.length === 0) return;

				const firstElement = focusableElements[0];
				const lastElement = focusableElements[focusableElements.length - 1];

				if (e.shiftKey) {
					if (document.activeElement === firstElement || document.activeElement === document.body) {
						e.preventDefault();
						lastElement.focus();
					}
				} else {
					if (document.activeElement === lastElement || document.activeElement === document.body) {
						e.preventDefault();
						firstElement.focus();
					}
				}
			}
		};

		if (reportToClose || reportToReopen || reportToActivate) {
			document.addEventListener('keydown', handleKeyDown);
			// Auto-focus the first button (Cancel) when the modal opens
			setTimeout(() => {
				const activeModal = reportToClose
					? closeModalRef
					: reportToReopen
						? reopenModalRef
						: reportToActivate
							? activateModalRef
							: null;
				const firstBtn = activeModal?.current?.querySelector('button');
				if (firstBtn && document.activeElement !== firstBtn) {
					firstBtn.focus();
				}
			}, 10);
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [reportToClose, reportToReopen, reportToActivate]);

	const { setReports, postMetadata, updatePostMetadata } = useData();
	const [isAutoRefreshEnabled] = useState<boolean>(true);
	const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
	// Auto-dismiss the "report no longer exists" toast.
	useEffect(() => {
		if (!toastMessage) return;
		const t = setTimeout(() => setToastMessage(null), 3000);
		return () => clearTimeout(t);
	}, [toastMessage]);
	const openReportsRef = useRef<HTMLDivElement>(null);
	const topRef = useRef<HTMLDivElement>(null);
	const bottomRef = useRef<HTMLDivElement>(null);
	const [showScrollToOpen, setShowScrollToOpen] = useState(false);
	const [showScrollToBottom, setShowScrollToBottom] = useState(false);
	const wasRestoredRef = useRef<boolean>(false);
	// Always points at the latest loaded list, so the 30s interval can read it
	// without being re-created on every appended page.
	const allReportDataRef = useRef<AllReportDataType[]>([]);

	const lastScrollY = useRef(0);
	const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Unified visibility check for all floating buttons (Top, Closed, Bottom)
	useEffect(() => {
		const checkVisibility = () => {
			const currentScrollY = window.scrollY;

			let isScrollingDown = false;
			let isScrollingUp = false;

			if (currentScrollY > lastScrollY.current) {
				isScrollingDown = true;
			} else if (currentScrollY < lastScrollY.current) {
				isScrollingUp = true;
			}

			lastScrollY.current = currentScrollY;

			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}

			let willShowTop = false;
			// 1. Go to Top button visibility
			if (currentScrollY <= 50) {
				setShowScrollToOpen(false);
			} else if (isScrollingUp) {
				setShowScrollToOpen(true);
				willShowTop = true;
			} else if (isScrollingDown) {
				setShowScrollToOpen(false);
			}

			let willShowBottom = false;
			// 2. Go to Bottom button visibility: check if there is more than 100px left to scroll
			const scrollRemaining = document.documentElement.scrollHeight - window.innerHeight - currentScrollY;
			if (scrollRemaining <= 100) {
				setShowScrollToBottom(false);
			} else if (isScrollingDown) {
				setShowScrollToBottom(true);
				willShowBottom = true;
			} else if (isScrollingUp) {
				setShowScrollToBottom(false);
			}

			if (willShowTop || willShowBottom) {
				hideTimeoutRef.current = setTimeout(() => {
					setShowScrollToOpen(false);
					setShowScrollToBottom(false);
				}, 2000);
			}
		};

		window.addEventListener('scroll', checkVisibility);
		window.addEventListener('resize', checkVisibility);

		// Observer to detect content height changes (like when reports load or tabs switch)
		const resizeObserver = new ResizeObserver(checkVisibility);
		resizeObserver.observe(document.body);

		// Initial and data-driven checks
		checkVisibility();
		const timer = setTimeout(checkVisibility, 100);
		const longerTimer = setTimeout(checkVisibility, 500);

		return () => {
			window.removeEventListener('scroll', checkVisibility);
			window.removeEventListener('resize', checkVisibility);
			resizeObserver.disconnect();
			clearTimeout(timer);
			clearTimeout(longerTimer);
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
		};
	}, [activeTab, AllReportData, isLoading, isFetchingMore]);

	// Helper functions

	const navigateToReport = (reportId: string) => {
		sessionStorage.setItem(`reportsScrollPos_${activeTab}`, window.scrollY.toString());
		sessionStorage.setItem(
			`reportsFeedState_${activeTab}`,
			JSON.stringify({
				AllReportData,
				page,
				hasMore,
			})
		);
		// Saved alongside the pages so Back restores the list and the box that
		// produced it together.
		if (searchQuery.trim()) {
			sessionStorage.setItem('reportsSearchQuery', searchQuery);
		} else {
			sessionStorage.removeItem('reportsSearchQuery');
		}
		navigate(`/getreport/${reportId}`);
	};

	// Handle Tab Change — reset the list and pagination; the activeTab effect
	// fetches page 1 of the newly selected tab.
	const handleTabChange = (tab: Tab) => {
		if (tab === activeTab) return;
		setActiveTab(tab);
		setSearchParams({ tab }); // Persist tab in URL search params
		set_AllReportData([]);
		setPage(1);
		setHasMore(true);
	};

	// Clean an authority name returned by the backend's fallback shape
	// ("Authority (<phone>)") so we don't leak phone numbers in the UI.
	const cleanName = (raw?: string | null): string => {
		if (!raw) return 'Unknown Authority';
		return raw.startsWith('Authority (') ? 'Unknown Authority' : raw;
	};

	// Process raw API results into our display format
	const processReports = (result: any[], tab: Tab): AllReportDataType[] => {
		return result
			.map((each) => {
				if (!each.media || each.media.length === 0 || !each.media[0].format) return null;
				if (each.media[0].format.substring(0, 5) !== 'image') return null;

				// Use the stored media name verbatim. (Previously we appended '.jpg'
				// to bridge extensionless names; the API's serveMedia already
				// resolves those, and once media moves to Blob+CDN — where objects
				// are stored under their exact name — appending '.jpg' would request
				// a non-existent object. So we stop appending now.)
				const internalURL = each.media[0].internalURL;

				// Card header avatar/name:
				//   sent  → show the original authority the user submitted to
				//   other → show the original reporter (user)
				const isSent = tab === 'sent';
				const originalAuthName = cleanName(each.originalAuthority?.name || each.authority?.name);
				const displayName = isSent
					? originalAuthName
					: each.isAnonymous
						? 'Anonymous'
						: each.user?.name || 'Unknown';
				const displayPic = isSent
					? each.originalAuthority?.profilepicURL || each.authority?.profilepicURL || ''
					: each.user?.profilepicURL || '';

				return {
					id: each.id,
					title: each.title,
					content: each.body || '',
					format: each.media[0].format.substring(0, 5),
					image_url: mediaUrl(internalURL),
					media_filename: each.media[0].internalURL,
					name: displayName,
					location: `(${each.media[0].latitude}, ${each.media[0].longitude})`,
					time: `${each.createdAt}`,
					commentCount: each.commentCount || 0,
					profilepicURL: displayPic,
					isClosed: each.isClosed || false,
					inProgress: each.inProgress || false,
					reportNumber: each.reportNumber || null,
					isAnonymous: isSent ? false : each.isAnonymous || false,
					forwardedFromName: each.forwardedFromAuthority?.name
						? cleanName(each.forwardedFromAuthority.name)
						: null,
					latestForwardedToName: each.latestForwardedToAuthority?.name
						? cleanName(each.latestForwardedToAuthority.name)
						: null,
					closedByName: each.closedByAuthority?.name
						? cleanName(each.closedByAuthority.name)
						: null,
					closedByUserId: each.closedByAuthority?.userId || null,
					closedAt: each.closedAt || null,
				} as AllReportDataType;
			})
			.filter((post): post is AllReportDataType => post !== null);
	};

	const PAGE_SIZE = 10;

	// Build a tab's endpoint URL for a given 1-based page. Every tab paginates
	// with limit/offset AND carries the active search term, so pagination and
	// filtering compose: scrolling a filtered tab pages through the matches only.
	const buildReportsUrl = (tab: Tab, targetPage: number): string =>
		buildSharedReportsUrl(tab, targetPage, PAGE_SIZE, activeSearchRef.current);

	// URL that re-fetches the first [count] already-loaded reports of a tab, used
	// for metadata-only refreshes (likes/comments/closed) that must NOT disturb
	// the infinite-scroll list or its pagination cursor. Same search term, or the
	// refresh would compare a filtered list against an unfiltered one.
	const buildRefreshUrl = (tab: Tab, count: number): string =>
		buildSharedReportsUrl(tab, 1, count, activeSearchRef.current);

	// Refresh the tab-label counts. Called on load, after every mutation, and
	// after a search settles — the numbers must never contradict the lists.
	const refreshCounts = async () => {
		const counts = await fetchTabCounts(activeSearchRef.current);
		if (counts) setTabCounts(counts);
	};

	// Monotonic request id — drops a stale response if the user switches tabs
	// while a fetch is still in flight.
	const requestIdRef = useRef<number>(0);

	// Fetch one page of a tab. targetPage === 1 replaces the list (initial load /
	// tab switch / refresh); later pages append (infinite scroll).
	const fetchReports = async (currentTab: Tab, targetPage: number) => {
		if (targetPage === 1) setIsLoading(true);
		else setIsFetchingMore(true);
		const myReqId = ++requestIdRef.current;
		try {
			const response = await fetch(buildReportsUrl(currentTab, targetPage), {
				credentials: 'include',
			});
			if (!response.ok) throw new Error('Failed to fetch reports');
			const result: ReportType[] = await response.json();

			// A newer request (tab switch) superseded this one — drop it.
			if (myReqId !== requestIdRef.current) return;

			const processed = processReports(result, currentTab);
			// A short page means there's nothing left to load.
			setHasMore(result.length >= PAGE_SIZE);

			let combined: AllReportDataType[];
			if (targetPage === 1) {
				combined = processed;
			} else {
				// Dedupe against what's already loaded — a report can shift across the
				// offset boundary if one was closed/forwarded out since the last page.
				const existingIds = new Set(AllReportData.map((r) => r.id));
				combined = [...AllReportData, ...processed.filter((r) => !existingIds.has(r.id))];
			}
			set_AllReportData(combined);
			if (currentTab === 'received' && targetPage === 1) setReports(combined);

			// Update global metadata registry
			processed.forEach((report) => {
				updatePostMetadata(report.id, {
					commentCount: report.commentCount,
					isClosed: report.isClosed,
				});
			});
		} catch (error) {
			console.error('Error fetching reports:', error);
		} finally {
			if (targetPage === 1) setIsLoading(false);
			setIsFetchingMore(false);
		}
	};

	const handleLoadMore = () => {
		const nextPage = page + 1;
		setPage(nextPage);
		fetchReports(activeTab, nextPage);
	};

	// Debounced server-side search. Every keystroke reschedules; only the term the
	// user settles on reaches the server. The list and pagination reset so the new
	// query starts from page 1, and the counts refresh alongside so the tab labels
	// describe the same filtered set the list is showing.
	//
	// Note there is NO minimum length here (unlike the news feed's 3 characters):
	// a report number can legitimately be "7", and these queries are already
	// narrowed to one authority's own reports.
	useEffect(() => {
		const effective = searchQuery.trim();
		if (isFirstSearchEffect.current) {
			isFirstSearchEffect.current = false;
			activeSearchRef.current = effective;
			refreshCounts();
			return;
		}
		if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		searchDebounceRef.current = setTimeout(() => {
			if (effective === activeSearchRef.current) return;
			activeSearchRef.current = effective;
			// Drop any cached per-tab pages: they were fetched for the old term.
			['sent', 'received', 'progress', 'active', 'closed'].forEach((t) => {
				sessionStorage.removeItem(`reportsFeedState_${t}`);
				sessionStorage.removeItem(`reportsScrollPos_${t}`);
			});
			set_AllReportData([]);
			setPage(1);
			setHasMore(true);
			fetchReports(activeTab, 1);
			refreshCounts();
		}, 300);
		return () => {
			if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);

	// Keep the ref in sync so the auto-refresh interval always sees the latest
	// loaded list without re-binding the timer on every appended page.
	useEffect(() => {
		allReportDataRef.current = AllReportData;
	}, [AllReportData]);

	// Background refresh of already-loaded reports. Primarily metadata-only
	// (likes / comments / closed flags), but it also performs SAFE removal
	// detection: we request every report we hold (capped at 100) and, if the
	// server returns fewer RAW reports than we asked for, it has handed back the
	// tab's entire set — so the fresh list is provably complete and can replace
	// ours, dropping reports another authority closed/forwarded out of this tab.
	// When the server returns a full window (equal length) we stay metadata-only,
	// since a newly-arrived report could otherwise push a still-valid one past
	// the window and cause a false removal. Never replaces while a page fetch is
	// in flight (snapshot-length guard).
	const refreshLoadedReports = async (currentTab: Tab, snapshot: AllReportDataType[]) => {
		if (snapshot.length === 0) return;
		const count = Math.min(snapshot.length, 100);
		try {
			const res = await fetch(buildRefreshUrl(currentTab, count), {
				credentials: 'include',
			});
			if (!res.ok) return;
			const result: ReportType[] = await res.json();
			if (!Array.isArray(result)) return;

			const processed = processReports(result, currentTab);
			// Always refresh metadata for whatever came back.
			processed.forEach((report) => {
				updatePostMetadata(report.id, {
					commentCount: report.commentCount,
					isClosed: report.isClosed,
				});
			});

			// Safe structural replace: a short RAW page proves we received the
			// complete tab. Skip if the list grew meanwhile (a page was appended),
			// so we don't clobber freshly-loaded pages.
			if (result.length < count && allReportDataRef.current.length === snapshot.length) {
				set_AllReportData(processed);
				if (currentTab === 'received') setReports(processed);
				setHasMore(false);
				setPage(Math.max(1, Math.ceil(processed.length / PAGE_SIZE)));
			}
		} catch (err) {
			console.error('Reports refresh error:', err);
		}
	};

	// Infinite scroll via IntersectionObserver — a sentinel near the list end
	// triggers the next page when it scrolls into view (400px early so the next
	// page is usually ready before the user reaches the bottom).
	useEffect(() => {
		const sentinel = loadMoreSentinelRef.current;
		if (!sentinel) return;
		if (!hasMore) return;
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry && entry.isIntersecting && !isFetchingMore && !isLoading) {
					handleLoadMore();
				}
			},
			{ rootMargin: '400px 0px' }
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasMore, isFetchingMore, isLoading, page, activeTab, AllReportData.length]);

	// Effect to fetch page 1 when the Tab changes (or restore a saved session
	// after a back-navigation, preserving the user's loaded depth).
	//
	// `showKanban` is a dependency, not just an early-return guard: while the
	// board is up this skips fetching, so on the way BACK the list would other-
	// wise still be empty and nothing would reload it — `activeTab` hasn't
	// changed. Depending on the flag makes leaving the board re-run the fetch.
	useEffect(() => {
		// The kanban owns its own fetching; don't load the list behind it.
		if (showKanban) return;
		const saved = sessionStorage.getItem(`reportsFeedState_${activeTab}`);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				if (parsed.AllReportData && parsed.AllReportData.length > 0) {
					set_AllReportData(parsed.AllReportData);
					setPage(parsed.page || 1);
					setHasMore(parsed.hasMore !== false);
					sessionStorage.removeItem(`reportsFeedState_${activeTab}`);
					wasRestoredRef.current = true;
					return;
				}
			} catch (e) { }
			sessionStorage.removeItem(`reportsFeedState_${activeTab}`);
		}

		setPage(1);
		setHasMore(true);
		fetchReports(activeTab, 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, showKanban]);

	// Scroll restoration using rAF polling.
	// IMPORTANT: removeItem is deferred inside tryScroll so that a mid-loop
	// data update (re-render) doesn't wipe the target before we've reached it.
	useEffect(() => {
		const savedScrollStr = sessionStorage.getItem(`reportsScrollPos_${activeTab}`);
		if (!savedScrollStr) return;
		const target = parseInt(savedScrollStr, 10);
		if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
		let rafId: number;
		let attempts = 0;
		const tryScroll = () => {
			attempts++;
			const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
			if (maxScroll >= target || attempts > 300) {
				// Clear the saved position only after we are actually scrolling to it
				sessionStorage.removeItem(`reportsScrollPos_${activeTab}`);
				window.scrollTo({ top: target, behavior: 'instant' as ScrollBehavior });
				// No further rAF — we're done
			} else {
				rafId = requestAnimationFrame(tryScroll);
			}
		};
		rafId = requestAnimationFrame(tryScroll);
		return () => cancelAnimationFrame(rafId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [AllReportData]);

	// Background Refresh on Restore — fires once after a sessionStorage restore
	// so a back-navigated list reflects any changes made while away.
	useEffect(() => {
		if (!wasRestoredRef.current) return;
		if (AllReportData.length === 0) return;
		wasRestoredRef.current = false;
		refreshLoadedReports(activeTab, AllReportData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, AllReportData.length]);

	// Auto-refresh interval (30s). Reads the latest list via a ref (so the timer
	// isn't reset on every appended page) and delegates to refreshLoadedReports,
	// which keeps metadata fresh and prunes reports that have left the tab when
	// it can do so safely.
	// Pauses when the tab is hidden to avoid browser-throttled background polling.
	useEffect(() => {
		const startPolling = () => {
			if (autoRefreshIntervalRef.current) clearInterval(autoRefreshIntervalRef.current);
			autoRefreshIntervalRef.current = setInterval(() => {
				refreshLoadedReports(activeTab, allReportDataRef.current);
				refreshCounts();
			}, 60000); // 60s (was 30s) — lighter standing load per open tab at scale.
		};

		const stopPolling = () => {
			if (autoRefreshIntervalRef.current) {
				clearInterval(autoRefreshIntervalRef.current);
				autoRefreshIntervalRef.current = null;
			}
		};

		const handleVisibilityChange = () => {
			if (document.hidden) {
				stopPolling();
			} else if (isAutoRefreshEnabled) {
				// Refresh immediately on re-focus, then resume interval.
				refreshLoadedReports(activeTab, allReportDataRef.current);
				startPolling();
			}
		};

		if (isAutoRefreshEnabled && !document.hidden && !showKanban) {
			startPolling();
		} else {
			stopPolling();
		}
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			stopPolling();
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAutoRefreshEnabled, activeTab, showKanban]);

	// Toggle close/reopen report via API.
	// Under the new tab model, closing/reopening always moves the card OUT of
	// the current tab (In Progress → Closed on close; Closed → In Progress on
	// reopen), so just drop it from the current list and let the destination tab
	// fetch fresh.
	const handleToggleClose = async (reportId: number) => {
		try {
			const response = await fetch(`${BACKEND_URL}/api/toggleReportClosed/${reportId}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			// Report was deleted (here or from another device) — drop the card and
			// tell the user instead of failing silently.
			if (response.status === 404) {
				set_AllReportData((prev) => prev.filter((r) => r.id !== reportId));
				setToastMessage('This report no longer exists.');
				setReportToClose(null);
				setReportToReopen(null);
				refreshCounts();
				return;
			}
			if (!response.ok) {
				// The server is the authority on the transition rules (e.g. closing a
				// report that another device already moved back to Received), so
				// surface its reason rather than a generic failure.
				const err = await response.json().catch(() => ({}));
				throw new Error(err.message || 'Failed to toggle report');
			}
			const result = await response.json();

			set_AllReportData((prev) => prev.filter((r) => r.id !== reportId));
			updatePostMetadata(reportId, { isClosed: !!result.isClosed });
			refreshCounts();
		} catch (error: any) {
			console.error('Error toggling report:', error);
			setToastMessage(error?.message || 'Could not update this report.');
		}
		setReportToClose(null);
		setReportToReopen(null);
	};

	// Activate a received report — "I've started working on this". Moves the card
	// from Received to In Progress and notifies the reporter (server-side).
	const handleActivate = async (reportId: number) => {
		try {
			const response = await fetch(`${BACKEND_URL}/api/startReportProgress/${reportId}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
			});
			if (response.status === 404) {
				set_AllReportData((prev) => prev.filter((r) => r.id !== reportId));
				setToastMessage('This report no longer exists.');
				setReportToActivate(null);
				refreshCounts();
				return;
			}
			if (!response.ok) {
				const err = await response.json().catch(() => ({}));
				throw new Error(err.message || 'Failed to start progress');
			}
			set_AllReportData((prev) => prev.filter((r) => r.id !== reportId));
			refreshCounts();
		} catch (error: any) {
			console.error('Error starting progress:', error);
			setToastMessage(error?.message || 'Could not start progress on this report.');
		}
		setReportToActivate(null);
	};

	const handleForwardSuccess = (reportId: number) => {
		// Forwarded report leaves Received/In Progress and joins Forwarded for the sender.
		set_AllReportData((prev) => prev.filter((r) => r.id !== reportId));
		refreshCounts();
	};

	// Helper to render a single report card
	const renderReportCard = (data: AllReportDataType) => {
		const formattedDate = (() => {
			try {
				const date = parseISO(data.time);
				return formatCustomDistanceToNow(date);
			} catch (e) {
				return data.time;
			}
		})();

		return (
			<article
				key={data.id}
				className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow'
				style={{
					// Skip paint/layout for off-screen cards so the list reveals
					// top-to-bottom as the user scrolls.
					contentVisibility: 'auto',
					containIntrinsicSize: 'auto 500px',
				}}
				onClick={() => navigateToReport(data.id.toString())}
			>
				{/* Report Header */}
				<div className='p-2.5 sm:p-4 border-b border-gray-100'>
					{/* Line 1: Author (bigger pfp + name over date) + Risk button,
					    kept vertically centered on this row. */}
					<div className='flex items-center justify-between gap-1.5 sm:gap-3'>
						<div className='flex items-center gap-3 sm:gap-4 min-w-0'>
							<ProfileImage
								name={data.name}
								pfpURL={data.profilepicURL}
								className='w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover border border-gray-200 shrink-0'
								isAnonymous={data.isAnonymous}
							/>
							{/* Name uses the former title size, sitting just above the date.
							    items-start keeps name and date individually left-aligned. */}
							<div className='flex flex-col items-start min-w-0'>
								<span className='font-bold text-gray-900 text-sm sm:text-xl leading-tight truncate text-left'>
									{data.name}
								</span>
								<div className='flex items-center text-[10px] sm:text-xs text-gray-500 gap-1 sm:gap-1.5'>
									<Clock className='w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 shrink-0' />
									<span className='truncate'>
										{activeTab === 'closed' && data.closedAt
											? formatCustomDistanceToNow(parseISO(data.closedAt))
											: formattedDate}
									</span>
								</div>
							</div>
						</div>
						<button
							className='flex items-center gap-1 px-1.5 py-0.5 sm:gap-1.5 sm:px-3 sm:py-1 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors text-[10px] sm:text-sm font-semibold border border-amber-100 shrink-0 self-center'
							onClick={(e) => {
								e.stopPropagation();
								if (data.media_filename) {
									setRiskoMeterMediaId(data.media_filename);
								}
							}}
							title='Risko-Meter'
						>
							<Gauge className='w-3 h-3 sm:w-4 sm:h-4' />
							<span>Risk</span>
						</button>
					</div>

					{/* Chain-status badge (Forwarded from/to, Closed by) — shown
					    above the title and horizontally centered. */}
					<div className='flex flex-wrap justify-center mt-3 empty:hidden'>
						{activeTab === 'received' && data.forwardedFromName && (
							<div className='inline-flex items-center text-[10px] sm:text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded-md font-medium border border-blue-100 shadow-sm min-w-0 max-w-full'>
								<Send className='w-3 h-3 mr-1 shrink-0' />
								<span className='truncate'>Forwarded from {data.forwardedFromName}</span>
							</div>
						)}
						{activeTab === 'active' && data.latestForwardedToName && (
							<div className='inline-flex items-center text-[10px] sm:text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded-md font-medium border border-blue-100 shadow-sm min-w-0 max-w-full'>
								<Send className='w-3 h-3 mr-1 shrink-0' />
								<span className='truncate'>Forwarded to {data.latestForwardedToName}</span>
							</div>
						)}
						{/* "Closed by X" only when SOMEONE ELSE closed it — telling an
						    authority their own name says nothing, and its absence is the
						    signal (here and on the board) that this is their own closure
						    and therefore theirs to reopen. */}
						{(activeTab === 'closed' || (activeTab === 'sent' && data.isClosed)) &&
							data.closedByName &&
							!closedByMe(data.closedByUserId, user?.id) && (
								<div className='inline-flex items-center text-[10px] sm:text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md font-medium border border-emerald-100 shadow-sm min-w-0 max-w-full'>
									<XCircle className='w-3 h-3 mr-1 shrink-0' />
									<span className='truncate'>Closed by {data.closedByName}</span>
								</div>
							)}
					</div>

					{/* Line 2: Title — now uses the former, smaller username size.
					    The report number sits at the END of this same line and is
					    `shrink-0`, so it stays visible even when a long title
					    ellipsizes (finding a report by its number is precisely when
					    the title is least useful). */}
					<div className='flex items-baseline gap-2 min-w-0 mt-3 sm:mt-4'>
						<h2
							className='text-[10px] sm:text-sm font-semibold text-gray-900 text-left truncate min-w-0'
							title={data.title || 'Untitled Report'}
						>
							{renderHighlightedText(data.title || 'Untitled Report', searchQuery)}
						</h2>
						<ReportNumberChip number={data.reportNumber} searchQuery={searchQuery} />
					</div>

					{/* Line 3: Description preview. */}
					{data.content && data.content.trim() !== '' && (
						<div className='mt-2'>
							<CardDescription text={data.content.trim()} />
						</div>
					)}
				</div>

				{/* Report Image */}
				<div className='relative'>
					<ReportImage src={data.image_url} alt='Report' className='w-full h-auto max-h-96 object-cover bg-gray-100' />
				</div>

				{/* Action Buttons (Footer) */}
				<div className='h-8 sm:h-12 shrink-0'>
					<div className='flex items-center justify-between h-full'>
						{/* Comments */}
						<button
							type='button'
							className='flex-1 flex items-center justify-center h-full text-gray-600 hover:bg-gray-50 border-t border-r border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black'
						>
							<div className='flex items-center relative'>
								{(postMetadata[data.id]?.commentCount ?? data.commentCount) > 0 && (
									<span className='absolute right-full mr-1 sm:mr-2 font-medium text-[10px] sm:text-sm whitespace-nowrap'>
										{postMetadata[data.id]?.commentCount ?? data.commentCount}
									</span>
								)}
								<MessageCircle className='w-3 h-3 sm:w-5 sm:h-5' />
								<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Comment</span>
							</div>
						</button>

						{/* Activate (Received tab). A received report can only be
						    forwarded or activated — never closed directly, so this
						    button takes the Close slot here. */}
						{activeTab === 'received' && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									setReportToActivate(data);
								}}
								className='flex-1 flex items-center justify-center h-full text-white bg-amber-500 hover:bg-amber-600 border-r border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black group'
							>
								<PlayCircle className='w-3 h-3 sm:w-5 sm:h-5 activate-icon-anim transition-transform' />
								<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Activate</span>
							</button>
						)}

						{/* Close (In Progress tab) — only an activated report can be closed */}
						{activeTab === 'progress' && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									setReportToClose(data);
								}}
								className='flex-1 flex items-center justify-center h-full text-white bg-red-600 hover:bg-red-700 border-r border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black group'
							>
								<XCircle className='w-3 h-3 sm:w-5 sm:h-5 close-icon-anim transition-transform' />
								<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Close Report</span>
							</button>
						)}

						{/* Reopen (Closed tab) — only if I'm the authority who closed this
						    report. */}
						{activeTab === 'closed' && data.isClosed && closedByMe(data.closedByUserId, user?.id) && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									setReportToReopen(data);
								}}
								className='flex-1 flex items-center justify-center h-full text-white bg-green-600 hover:bg-green-700 border-r border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black group'
							>
								<RotateCcw className='w-3 h-3 sm:w-5 sm:h-5 reopen-icon-anim transition-transform' />
								<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Reopen Report</span>
							</button>
						)}

						{/* Forward / View Report — forwarding is allowed from both open
						    states (Received and In Progress). */}
						{activeTab === 'received' || activeTab === 'progress' ? (
							<button
								type='button'
								onClick={(e) => {
									e.stopPropagation();
									setSelectedReportIdForForward(data.id);
									setIsForwardModalOpen(true);
								}}
								className='flex-1 flex items-center justify-center h-full border-t border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black group text-blue-600 hover:bg-blue-50'
							>
								<span className='text-[10px] sm:text-sm font-medium'>Forward Report</span>
								<span className='relative overflow-hidden rounded-full inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 ml-1 sm:ml-2 shrink-0'>
									<Send className='w-3.5 h-3.5 sm:w-4 sm:h-4 telegram-send-icon' />
								</span>
							</button>
						) : (
							<button
								type='button'
								className='flex-1 flex items-center justify-center h-full text-gray-600 hover:bg-gray-50 border-t border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black group'
							>
								<span className='text-[10px] sm:text-sm font-medium'>View Report</span>
								<span className='ml-1 sm:ml-2 inline-block group-hover:translate-x-1 transition-transform text-[10px] sm:text-sm'>→</span>
							</button>
						)}
					</div>
				</div>
			</article>
		);
	};

	return (
		<div ref={topRef} className='min-h-screen bg-gray-100'>
			{/* Transient toast — e.g. acting on a report that no longer exists */}
			{toastMessage && (
				<div className='fixed top-6 left-1/2 -translate-x-1/2 z-[1100] bg-gray-900 text-white px-5 py-3 rounded-xl text-sm font-semibold shadow-2xl'>
					{toastMessage}
				</div>
			)}

			{/* Header */}
			<Header />

			{/* View switch. Rendered OUTSIDE the branch below and anchored to the
			    viewport, so it holds the same spot in both views and switching
			    doesn't move the control out from under the cursor. */}
			{user?.is_authority && isDesktop && (
				<ViewModeSwitch isKanban={showKanban} onChange={setViewMode} />
			)}

			{/* Main Container */}
			<div className='pt-[var(--header-height,80px)]'>
				{showKanban ? (
					// Interactive Dashboard. Exactly the same data and the same rules as
					// the list below — only the arrangement differs — so a change made
					// here shows up there and vice versa (both read the server).
					<ReportsKanban
						currentUserId={user?.id ? Number(user.id) : undefined}
						currentAuthorityPhone={user?.phoneNumber ? Number(user.phoneNumber) : undefined}
						currentUserName={user?.name}
						onOpenReport={(id) => navigate(`/getreport/${id}`)}
					/>
				) : (
					<div className='flex justify-center'>
						<div className='w-full max-w-2xl px-6 py-6'>
							{/* Welcome Header */}
							<div className='mb-6'>
								<h1 className='m-0 text-2xl font-bold leading-tight text-gray-900 text-left'>Hello {user?.name},</h1>
								<p className='m-0 mt-1 leading-tight text-gray-600 text-left'>Manage and review your reports</p>
							</div>

							{/* Search — deliberately ABOVE the tabs, because the term applies
						    to all of them: type once, then switch tabs to find where
						    the report actually is. */}
							<div className='mb-4'>
								<div className='relative'>
									<Search className='absolute left-[10px] top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
									<input
										type='text'
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										placeholder={
											user?.is_authority ? 'Search by Title or Report number' : 'Search by title'
										}
										className='w-full pl-10 sm:pl-12 pr-14 sm:pr-20 py-1.5 sm:py-2 bg-white border border-gray-200 rounded-full
                                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                        shadow-sm hover:shadow-md transition-shadow text-gray-900 text-[13px] sm:text-base'
									/>
									{searchQuery && (
										<button
											onClick={() => setSearchQuery('')}
											className='absolute right-[6px] top-1/2 -translate-y-1/2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-500 text-[10px] font-bold rounded-full transition-colors border border-gray-200 flex items-center justify-center leading-none'
											style={{ height: 'calc(100% - 12px)' }}
										>
											Clear
										</button>
									)}
								</div>
							</div>

							{/* Tab Navigation. The count in brackets is the total number of
						    reports in that tab UNDER THE CURRENT SEARCH — server-computed,
						    so it counts every match, not just the pages loaded so far.
						    Wrapped in DragScrollRow: five tabs overflow a phone, and this
						    pans by dragging (mouse or finger) with faded ends instead of
						    showing a scrollbar. */}
							<div className='mb-6 border-y border-gray-200'>
								<DragScrollRow className='flex space-x-0 select-none py-0 px-1'>
									{user?.is_authority &&
										(
											[
												['received', <Download className="w-4 h-4 sm:w-5 sm:h-5" />],
												['progress', <Hourglass className="w-4 h-4 sm:w-5 sm:h-5" />],
												['closed', <XCircle className="w-4 h-4 sm:w-5 sm:h-5" />],
												['active', <Send className="w-4 h-4 sm:w-5 sm:h-5" />],
											] as [Tab, JSX.Element][]
										).map(([tab, icon]) => (
											<button
												key={tab}
												onClick={() => handleTabChange(tab)}
												className={`relative group flex items-center gap-1.5 sm:gap-2 py-1.5 px-2 sm:py-2.5 sm:px-3 text-sm sm:text-base transition-colors font-medium whitespace-nowrap rounded-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black ${activeTab === tab ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
													}`}
											>
												{icon}
												{TAB_LABELS[tab]}
												<span className='text-[10px] sm:text-xs font-semibold text-gray-400'>
													({countForTab(tabCounts, tab)})
												</span>
												<span
													className={`absolute bottom-0 inset-x-0 h-[2px] transition-colors ${activeTab === tab
														? 'bg-blue-600'
														: 'bg-transparent group-hover:bg-gray-300'
														}`}
												/>
											</button>
										))}
									<button
										onClick={() => handleTabChange('sent')}
										className={`relative group flex items-center gap-1.5 sm:gap-2 py-1.5 px-2 sm:py-2.5 sm:px-3 text-sm sm:text-base transition-colors font-medium whitespace-nowrap rounded-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black ${activeTab === 'sent' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
											}`}
									>
										<Upload className="w-4 h-4 sm:w-5 sm:h-5" />
										{user?.is_authority ? 'Sent' : 'Reports Sent'}
										<span className='text-[10px] sm:text-xs font-semibold text-gray-400'>({tabCounts.sent})</span>
										<span
											className={`absolute bottom-0 inset-x-0 h-[2px] transition-colors ${activeTab === 'sent'
												? 'bg-blue-600'
												: 'bg-transparent group-hover:bg-gray-300'
												}`}
										/>
									</button>
								</DragScrollRow>
							</div>

							{/* Reports Feed */}
							<div className=''>
								{isLoading && AllReportData.length === 0 ? (
									<div className='text-center py-12'>
										<div className='text-gray-500'>Loading reports...</div>
									</div>
								) : AllReportData.length === 0 && !isLoading ? (
									<div className='text-center py-12'>
										<p className='text-gray-500'>
											{searchQuery.trim()
												? `No reports in ${TAB_LABELS[activeTab]} match “${searchQuery.trim()}”.`
												: activeTab === 'received'
													? 'No new reports waiting on you.'
													: activeTab === 'progress'
														? 'No reports in progress. Activate a received report to start working on it.'
														: activeTab === 'active'
															? 'No active forwarded reports.'
															: activeTab === 'closed'
																? 'No closed reports yet.'
																: 'No sent reports found.'}
										</p>
									</div>
								) : (
									<div ref={openReportsRef} className='scroll-mt-24 flex flex-col gap-8'>
										{AllReportData.map((data, idx) => (
											<React.Fragment key={data.id || idx}>
												{renderReportCard(data)}
											</React.Fragment>
										))}

										{/* Infinite-scroll sentinel — IntersectionObserver triggers the next
									    page when this scrolls into view. */}
										{hasMore && (
											<div ref={loadMoreSentinelRef} className='py-6 flex justify-center'>
												{isFetchingMore && (
													<div className='w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin' />
												)}
											</div>
										)}

										{/* End-of-list marker once every page has been loaded. */}
										{!hasMore && AllReportData.length > 0 && (
											<div className='py-6 text-center text-sm text-gray-400'>
												You've reached the end.
											</div>
										)}

										{/* Sentinel for the Go-to-Bottom button. Using a ref + scrollIntoView
									    forces the browser to lay out the path to this node, materializing any
									    content-visibility: auto cards along the way — avoids the small overshoot
									    that scrollTo(scrollHeight) would otherwise cause on first load. */}
										<div ref={bottomRef} aria-hidden='true' style={{ height: 1 }} />
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</div>

			{riskoMeterMediaId && <RiskoMeter mediaId={riskoMeterMediaId} onClose={() => setRiskoMeterMediaId(null)} />}

			{/* Activate Report Confirmation Modal */}
			{reportToActivate && (
				<div
					className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'
					onClick={() => setReportToActivate(null)}
				>
					<div
						ref={activateModalRef}
						className='bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in duration-200'
						onClick={(e) => e.stopPropagation()}
					>
						<div className='p-6 text-center'>
							<div className='w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4'>
								<PlayCircle className='w-8 h-8 text-amber-600' />
							</div>
							<h3 className='text-xl font-bold text-gray-900 mb-2'>Start working on this report?</h3>
							<p className='text-gray-600 text-sm mb-6 leading-relaxed'>
								This moves the report to <strong>In Progress</strong> and notifies the reporter that
								you've started working on it.
								<br />
								You'll be able to close it once it's in progress.
							</p>
							<div className='flex gap-3'>
								<button
									onClick={() => setReportToActivate(null)}
									className='flex-1 px-4 py-2.5 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors'
								>
									Cancel
								</button>
								<button
									onClick={() => handleActivate(reportToActivate.id)}
									className='flex-1 px-4 py-2.5 rounded-xl text-white bg-amber-500 hover:bg-amber-600 font-semibold transition-colors shadow-lg shadow-amber-200'
								>
									Activate
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Close Report Confirmation Modal */}
			{reportToClose && (
				<div
					className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'
					onClick={() => setReportToClose(null)}
				>
					<div
						ref={closeModalRef}
						className='bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in duration-200'
						onClick={(e) => e.stopPropagation()}
					>
						<div className='p-6 text-center'>
							<div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
								<XCircle className='w-8 h-8 text-red-600' />
							</div>
							<h3 className='text-xl font-bold text-gray-900 mb-2'>Close Report?</h3>
							<p className='text-gray-600 text-sm mb-6 leading-relaxed'>
								Are you sure you want to close this report? The reporter and other authorities involved will be able to see that you closed it.<br />You can reopen the report anytime.
							</p>
							<div className='flex gap-3'>
								<button
									onClick={() => setReportToClose(null)}
									className='flex-1 px-4 py-2.5 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors'
								>
									Cancel
								</button>
								<button
									onClick={() => handleToggleClose(reportToClose.id)}
									className='flex-1 px-4 py-2.5 rounded-xl text-white bg-red-600 hover:bg-red-700 font-semibold transition-colors shadow-lg shadow-red-200'
								>
									Close
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Reopen Report Confirmation Modal */}
			{reportToReopen && (
				<div
					className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'
					onClick={() => setReportToReopen(null)}
				>
					<div
						ref={reopenModalRef}
						className='bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in duration-200'
						onClick={(e) => e.stopPropagation()}
					>
						<div className='p-6 text-center'>
							<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
								<RotateCcw className='w-8 h-8 text-green-600' />
							</div>
							<h3 className='text-xl font-bold text-gray-900 mb-2'>Reopen Report?</h3>
							<p className='text-gray-600 text-sm mb-6 leading-relaxed'>
								Are you sure you want to reopen this report? It will become active again for you.
							</p>
							<div className='flex gap-3'>
								<button
									onClick={() => setReportToReopen(null)}
									className='flex-1 px-4 py-2.5 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors'
								>
									Cancel
								</button>
								<button
									onClick={() => handleToggleClose(reportToReopen.id)}
									className='flex-1 px-4 py-2.5 rounded-xl text-white bg-green-600 hover:bg-green-700 font-semibold transition-colors shadow-lg shadow-green-200'
								>
									Reopen
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<ForwardReportModal
				isOpen={isForwardModalOpen}
				onClose={() => {
					setIsForwardModalOpen(false);
					setSelectedReportIdForForward(null);
				}}
				reportId={selectedReportIdForForward}
				currentAuthorityPhone={user?.phoneNumber ? Number(user.phoneNumber) : undefined}
				currentUserId={user?.id ? Number(user.id) : undefined}
				onForwardSuccess={handleForwardSuccess}
			/>

			{/* Floating "Go to Top" Button (Calculated Symmetry) */}
			<div
				className={`go-to-top-button-wrap ${showScrollToOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-[120px] pointer-events-none'
					}`}
			>
				<button
					onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
					className='go-to-top-button bg-blue-600 text-white w-12 h-12 rounded-full shadow-2xl flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all'
					title='Go to Top'
				>
					<ArrowUp size={24} />
				</button>
			</div>

			{/* Floating "Go to Bottom" Button (Absolute Page Bottom) */}
			<div
				className={`go-to-bottom-page-wrap ${showScrollToBottom ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[120px] pointer-events-none'
					}`}
			>
				<button
					onClick={() =>
						bottomRef.current
							? bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
							: window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
					}
					className='go-to-bottom-page-button bg-blue-600 text-white w-12 h-12 rounded-full shadow-2xl flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all'
					title='Go to Bottom'
				>
					<ArrowDown size={24} />
				</button>
			</div>
		</div>
	);
}

export default MyReports;
